import { createSlice, PayloadAction, Dispatch } from '@reduxjs/toolkit';
import { APIError } from 'models';
import {
  ConnectivityState,
  DeviceFilter,
  DevicesSuccess,
  DeviceDetailsSuccess,
  DigitalTwinSuccess,
  DeviceErrorsSuccess,
  DeviceShadowSuccess,
  DeviceUpdateSuccess,
  FavoriteDeviceUpdateSuccess,
  Device,
  AnnotationsSuccess,
  DesiredValue,
} from 'models/connectivity';
import {
  findDevices,
  getCachedAnnotations,
  getDetailsForDevice,
  getDeviceErrors,
  getDeviceShadow,
  getDigitalTwin,
  setDesired,
  undesire,
  updateDeviceAttributes,
  updateFavoriteDeviceAttribute,
} from 'services/connectivityService/connectivityService';
import type { RootState } from 'store/store';
import { getFeature } from 'utils/connectivity';

const EMPTY_SHADOW = { data: { attributes: { features: [], rawData: '' } } };

export const defaultState: ConnectivityState = {
  list: {
    data: null,
    totalCount: 0,
    lastEvaluatedId: null,
    onlyFavorites: false,
    isLoading: false,
    error: null,
    filters: {
      active: false,
      data: null,
    },
  },
  details: {
    deviceInfo: null,
    deviceShadow: {
      data: null,
      isLoading: false,
      error: null,
      exportData: null,
    },
    digitalTwin: {
      data: null,
      isLoading: false,
      error: null,
    },
    deviceErrors: {
      data: null,
      isLoading: false,
      error: null,
    },
    annotations: {
      data: null,
      isLoading: false,
      error: null,
    },
    update: { isLoading: false, error: null },
    isLoading: false,
    error: null,
    feature: null,
    desired: { isLoading: false, error: null, data: null },
  },
};

// Export slice
export const devicesSlice = createSlice({
  name: 'connectivity',
  initialState: defaultState,
  reducers: {
    getDevices: (state) => {
      state.list.isLoading = true;
      state.list.error = null;
    },
    getDevicesSuccess: (state, action: PayloadAction<DevicesSuccess>) => {
      state.list.isLoading = false;
      state.list.error = null;
      state.list.data = action.payload.response;
      state.list.totalCount = action.payload.totalCount;
      state.list.lastEvaluatedId = action.payload.lastEvaluatedId || null;
      state.list.onlyFavorites = action.payload.onlyFavorites;
      state.list.filters.data = action.payload.filters;
    },
    getDevicesFailure: (state, action: PayloadAction<APIError>) => {
      state.list.data = [];
      state.list.totalCount = 0;
      state.list.lastEvaluatedId = null;
      state.list.isLoading = false;
      state.list.error = action.payload;
    },
    setDeviceFilters: (state, action: PayloadAction<boolean>) => {
      state.list.filters.active = action.payload;
    },
    getDeviceDetails: (state) => {
      state.details.isLoading = true;
      state.details.error = null;
      state.details.deviceInfo = null;
      state.details.feature = null;
    },
    getDeviceDetailsSuccess: (
      state,
      action: PayloadAction<DeviceDetailsSuccess>
    ) => {
      state.details.isLoading = false;
      state.details.deviceInfo = action.payload.deviceInfo;
      state.details.feature = action.payload.feature;
    },
    getDeviceDetailsFailure: (state, action: PayloadAction<APIError[]>) => {
      state.details.deviceInfo = null;
      state.details.feature = null;
      state.details.isLoading = false;
      state.details.error = action.payload;
    },
    getShadow: (state) => {
      state.details.deviceShadow.isLoading = true;
      state.details.deviceShadow.error = null;
      state.details.deviceShadow.data = null;
      state.details.deviceShadow.exportData = null;
    },
    getShadowSuccess: (state, action: PayloadAction<DeviceShadowSuccess>) => {
      state.details.deviceShadow.isLoading = false;
      state.details.deviceShadow.data = action.payload.deviceShadow;
      state.details.deviceShadow.exportData = action.payload.exportData;
      state.details.desired.data = action.payload.desired;
    },
    getShadowFailure: (state, action: PayloadAction<APIError>) => {
      state.details.deviceShadow.isLoading = false;
      state.details.deviceShadow.data = null;
      state.details.deviceShadow.exportData = null;
      state.details.deviceShadow.error = action.payload;
    },
    getDigitalTwinStart: (state) => {
      state.details.digitalTwin.isLoading = true;
      state.details.digitalTwin.error = null;
      state.details.digitalTwin.data = null;
    },
    getDigitalTwinSuccess: (
      state,
      action: PayloadAction<DigitalTwinSuccess>
    ) => {
      state.details.digitalTwin.isLoading = false;
      state.details.digitalTwin.error = null;
      state.details.digitalTwin.data = action.payload.digitalTwin;
    },
    getDigitalTwinFailure: (state, action: PayloadAction<APIError>) => {
      state.details.digitalTwin.isLoading = false;
      state.details.digitalTwin.error = action.payload;
      state.details.digitalTwin.data = null;
    },
    getDeviceErrorsStart: (state) => {
      state.details.deviceErrors.isLoading = true;
      state.details.deviceErrors.error = null;
      state.details.deviceErrors.data = null;
    },
    getDeviceErrorsSuccess: (
      state,
      action: PayloadAction<DeviceErrorsSuccess>
    ) => {
      state.details.deviceErrors.isLoading = false;
      state.details.deviceErrors.error = null;
      state.details.deviceErrors.data = action.payload.deviceErrors;
    },
    getDeviceErrorsFailure: (state, action: PayloadAction<APIError>) => {
      state.details.deviceErrors.isLoading = false;
      state.details.deviceErrors.error = action.payload;
      state.details.deviceErrors.data = null;
    },
    getAnnotations: (state) => {
      state.details.annotations.isLoading = true;
      state.details.annotations.error = null;
      state.details.annotations.data = null;
    },
    getAnnotationsSuccess: (
      state,
      action: PayloadAction<AnnotationsSuccess>
    ) => {
      state.details.annotations.isLoading = false;
      state.details.annotations.error = null;
      state.details.annotations.data = {
        systems: action.payload.data.systems || null,
        slsEnabled: action.payload.data.slsEnabled || null,
        adapter: action.payload.data.adapter || null,
        instanceId: action.payload.data.instanceId || null,
      };
    },
    getAnnotationsFailure: (state, action: PayloadAction<APIError>) => {
      state.details.annotations.isLoading = false;
      state.details.annotations.error = action.payload;
      state.details.annotations.data = null;
    },
    updateDevice: (state) => {
      state.details.update.isLoading = true;
      state.details.update.error = null;
    },
    updateDeviceSuccess: (
      state,
      action: PayloadAction<DeviceUpdateSuccess>
    ) => {
      state.details.update.isLoading = false;
      state.details.update.error = null;
      if (state.details.deviceInfo) {
        state.details.deviceInfo.attributes.isTestDevice =
          action.payload.isTestDevice;
        state.details.deviceInfo.attributes.testDeviceDescription =
          action.payload.description;
      }
    },
    updateDeviceFailure: (state, action: PayloadAction<APIError>) => {
      state.details.update.isLoading = false;
      state.details.update.error = action.payload;
    },
    updateFavoriteDevice: (state) => {
      state.list.error = null;
    },
    updateFavoriteDeviceSuccess: (
      state,
      action: PayloadAction<FavoriteDeviceUpdateSuccess>
    ) => {
      state.list.isLoading = false;
      state.list.error = null;

      state.list.data?.forEach((device) => {
        if (device.id === action.payload.iprId) {
          device.attributes.isFavoriteDevice = action.payload.isFavoriteDevice;
        }
      });
    },
    setDesiredFailure: (state, action: PayloadAction<APIError>) => {
      state.details.desired.isLoading = false;
      state.details.desired.error = action.payload;
    },
    setDesiredSuccess: (state, action: PayloadAction<unknown>) => {
      console.log('setDesiredSuccess action', action);
      state.details.desired.isLoading = false;
      state.details.desired.error = null;
    },
    getDesiredSuccess: (
      state,
      action: PayloadAction<{ deviceShadow: any }>
    ) => {
      state.details.desired.isLoading = false;
      state.details.desired.error = null;
      state.details.desired.data = action.payload.deviceShadow;
    },
    resetDesiredState: (state) => {
      state.details.desired = { isLoading: false, error: null, data: null };
    },
  },
});

// Export actions
export const {
  getDevices,
  getDevicesSuccess,
  getDevicesFailure,
  setDeviceFilters,
  getDeviceDetails,
  getDeviceDetailsSuccess,
  getDeviceDetailsFailure,
  getDigitalTwinStart,
  getDigitalTwinSuccess,
  getDigitalTwinFailure,
  getDeviceErrorsStart,
  getDeviceErrorsSuccess,
  getDeviceErrorsFailure,
  getAnnotations,
  getAnnotationsSuccess,
  getAnnotationsFailure,
  getShadow,
  getShadowSuccess,
  getShadowFailure,
  updateDevice,
  updateDeviceSuccess,
  updateDeviceFailure,
  updateFavoriteDevice,
  updateFavoriteDeviceSuccess,
  getDesiredSuccess,
  setDesiredFailure,
  setDesiredSuccess,
  resetDesiredState,
} = devicesSlice.actions;

// Export reducer
export const connectivityReducer = devicesSlice.reducer;

export const connectivityListSelector = (state: RootState) =>
  state.connectivity.list;
export const connectivityDetailsSelector = (state: RootState) =>
  state.connectivity.details;
export const updateDeviceSelector = (state: RootState) =>
  state.connectivity.details.update;

// Export thunk
export const fetchDeviceList =
  /* prettier-ignore */
  (filter: DeviceFilter[], offset: number, startId?: string) => async (dispatch: Dispatch) => {
    dispatch(getDevices());
    try {
      const limit = 10;
      const response = await findDevices(offset, limit, filter, startId);
      dispatch(
        getDevicesSuccess({
          response: response.data,
          totalCount: response.meta.totalCount,
          lastEvaluatedId: response.meta.lastEvaluatedId,
          onlyFavorites: response.meta.onlyFavorites,
          filters: response.data.length ? filter : null,
        })
      );
    } catch (e: any) {
      dispatch(getDevicesFailure(e.errors?.[0] || e[0] || e));    }
  };

export const fetchDeviceListFromStoredPage =
  /* prettier-ignore */
  (filter: DeviceFilter[], data: Device[], totalCount: number, lastEvaluatedId: string, onlyFavorites: boolean) => async (dispatch: Dispatch) => {
    dispatch(getDevices());
    try {
      dispatch(
        getDevicesSuccess({
          response: data,
          totalCount: totalCount,
          lastEvaluatedId: lastEvaluatedId,
          onlyFavorites,
          filters: filter,
        })
      );
    } catch (e: any) {
      dispatch(getDevicesFailure(e));
    }
  };

export const fetchDeviceDetails =
  (iprId: string) => async (dispatch: Dispatch) => {
    dispatch(getDeviceDetails());
    try {
      const deviceResponse = await getDetailsForDevice(iprId);
      const feature = getFeature(deviceResponse?.data[0]?.attributes?.features);
      dispatch(
        getDeviceDetailsSuccess({
          deviceInfo: deviceResponse.data[0],
          feature,
        })
      );
    } catch (e: APIError | any) {
      dispatch(getDeviceDetailsFailure(e.errors || e));
    }
  };

export const fetchDeviceShadow =
  (iprId: string, feature: string, specificationId: string) =>
  async (dispatch: Dispatch) => {
    dispatch(getShadow());
    try {
      const shadowResponse = await getDeviceShadow(
        iprId,
        feature,
        specificationId
      );
      dispatch(
        getShadowSuccess({
          deviceShadow: shadowResponse.data.attributes.features || EMPTY_SHADOW,
          exportData: JSON.stringify(shadowResponse.data.attributes.rawData),
          desired: shadowResponse.data.attributes.desired || [],
        })
      );
    } catch (e: APIError | APIError[] | any) {
      dispatch(getShadowFailure((e.errors?.[0] || e[0] || e)));
    }
  };

export const fetchDigitalTwin =
  (iprId: string) => async (dispatch: Dispatch) => {
    dispatch(getDigitalTwinStart());
    try {
      const digitalTwinResponse = await getDigitalTwin(iprId);
      dispatch(
        getDigitalTwinSuccess({
          digitalTwin: digitalTwinResponse.data.attributes,
        })
      );
    } catch (e: APIError | any) {
      dispatch(getDigitalTwinFailure(e.errors[0] || e));
    }
  };

export const fetchDeviceErrors =
  (iprId: string, start: string, end: string) => async (dispatch: Dispatch) => {
    dispatch(getDeviceErrorsStart());
    try {
      const deviceErrorsResponse = await getDeviceErrors(iprId, start, end);
      dispatch(
        getDeviceErrorsSuccess({
          deviceErrors: deviceErrorsResponse.data.attributes.errors,
        })
      );
    } catch (e: APIError | any) {
      dispatch(getDeviceErrorsFailure(e.errors[0] || e));
    }
  };

export const fetchAnnotations =
  (iprId: string) => async (dispatch: Dispatch) => {
    dispatch(getAnnotations());
    try {
      const annotationsResponse = await getCachedAnnotations(iprId);
      dispatch(
        getAnnotationsSuccess({
          data: annotationsResponse.data.attributes,
        })
      );
    } catch (e: APIError | any) {
      dispatch(getAnnotationsFailure(e.errors[0] || e));
    }
  };

export const storeDeviceUpdates =
  /* prettier-ignore */
  (iprId: string, isTestDevice: boolean, description: string, pnc: string) => async (dispatch: Dispatch) => {
    dispatch(updateDevice());
    try {
      const response = await updateDeviceAttributes(iprId, isTestDevice, description, pnc);
      dispatch(updateDeviceSuccess({
        isTestDevice: response.data.attributes.isTestDevice,
        description: response.data.attributes.description,
      }));
    } catch (e: any) {
      dispatch(updateDeviceFailure(e.errors || e));
    }
  };

export const storeFavoriteDevice =
  (iprId: string, isFavoriteDevice: boolean) => async (dispatch: Dispatch) => {
    dispatch(updateFavoriteDevice());
    try {
      const response = await updateFavoriteDeviceAttribute(
        iprId,
        isFavoriteDevice
      );
      dispatch(
        updateFavoriteDeviceSuccess({
          iprId: response.data.id,
          isFavoriteDevice: response.data.attributes.isFavoriteDevice,
        })
      );
    } catch (e: any) {
      dispatch(getDevicesFailure(e.errors || e));
    }
  };

export const storeDesiredValues =
  (
    iprId: string,
    feature: string,
    specificationId: string,
    desiredValues: DesiredValue[],
    specialDataControl: boolean,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      await setDesired(iprId, feature, specificationId, desiredValues, specialDataControl);
      dispatch(setDesiredSuccess({}));
    } catch (e: any) {
      dispatch(setDesiredFailure(e.errors || e));
    }
  };

export const removeDesiredValues =
  (iprId: string, feature: string, resourceId: string | undefined) =>
  async (dispatch: Dispatch) => {
    try {
      await undesire(iprId, feature, resourceId);
      dispatch(setDesiredSuccess({}));
    } catch (e: any) {
      dispatch(setDesiredFailure(e.errors || e));
    }
  };
